import useCurrentUserPaymentData from "../hooks/useCurrentUserPaymentData";
import { SecondaryButton } from "./buttons/SecondaryButton";
import { COLOR_APP_TEXT, FONT_SIZE_SMALL } from "./theme";
import styled from "@emotion/styled";
import { useRef, useState } from "react";
import useIsMobile from "../hooks/useIsMobile";
import { IconButton } from "./buttons/IconButton";
import AttorneyServicesCard from "./layout/AttorneyServicesCard";
import { useGetAnswersWithAttorneyRestrictionsQuery } from "../api/answers";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetCartsByUserQuery, useGetProductsQuery } from "../api/payments";
import AttorneyServicesUnpaid from "./AttorneyServicesUnpaid";
import Attorney from "./icons/Attorney";

export const StyledAttorneySecondaryButton = styled(SecondaryButton)`
  width: fit-content;
  color: ${COLOR_APP_TEXT};
  font-size: ${FONT_SIZE_SMALL};
  font-weight: 400;
  gap: 8px;
  padding: 8px 16px;
  transition: opacity 300ms ease-in-out;
`;

const AttorneyServicesComponent = () => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const {
    user,
    document,
    isPrenupPaid,
    currentUserWillHireAttorney,
    representationPayment,
    isRepresentationBooked,
    userCanPurchaseRepresentation,
    fianceCanPurchaseRepresentation,
    maxRepresentationProductsCanPurchase,
    qAPayment,
    isQAWaiting,
    isQAScheduled,
    isQAProductAvailable,
    isLoading: isUserLoading,
    error: userError,
    userDocumentError,
    userDocumentLoading,
  } = useCurrentUserPaymentData();

  const {
    data: answersWithAttorneyRestrictions,
    isLoading: isAnswersWithAttorneyRestrictionsLoading,
    error: answersWithAttorneyRestrictionsError,
  } = useGetAnswersWithAttorneyRestrictionsQuery(
    user && document?.stateId && !userDocumentLoading && !userDocumentError
      ? { userId: user.id }
      : skipToken,
  );

  const {
    data: products,
    isLoading: isProductsLoading,
    error: isProductsError,
  } = useGetProductsQuery();
  const {
    data: carts,
    isLoading: isCartsLoading,
    error: cartsError,
  } = useGetCartsByUserQuery(
    user && document?.stateId && !userDocumentLoading && !userDocumentError
      ? { id: user.id, transactionStatus: "pending" }
      : skipToken,
  );

  const isLoading =
    isUserLoading ||
    isAnswersWithAttorneyRestrictionsLoading ||
    isProductsLoading ||
    isCartsLoading;
  const hasMissingData =
    !document ||
    !user ||
    !answersWithAttorneyRestrictions ||
    !products ||
    !carts;
  const error =
    answersWithAttorneyRestrictionsError ??
    userError ??
    isProductsError ??
    cartsError;
  const isVisible = document?.state.attorneySupport === "PARTNER";
  const isQAPending = isQAWaiting || isQAScheduled;
  const noMoreServicesAvailable = isRepresentationBooked && !isQAPending;

  if (error || !isVisible || hasMissingData || noMoreServicesAvailable) {
    return null;
  }

  if (isMobile) {
    return !isPrenupPaid ? (
      <AttorneyServicesUnpaid
        isMobile={isMobile}
        isLoading={isLoading}
        stateName={document.state.name}
      />
    ) : (
      <>
        <IconButton
          data-testid="attorney-services-button"
          ref={buttonRef}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <Attorney />
        </IconButton>
        {isOpen && (
          <AttorneyServicesCard
            data-testid="attorney-services-card"
            user={user}
            currentUserWillHireAttorney={!!currentUserWillHireAttorney}
            products={products}
            setIsOpen={setIsOpen}
            buttonRef={buttonRef}
            userDocument={document}
            answersWithAttorneyRestrictions={answersWithAttorneyRestrictions}
            representationPayment={representationPayment}
            qAPayment={qAPayment}
            isQAPending={isQAPending}
            userCanPurchaseRepresentation={userCanPurchaseRepresentation}
            fianceCanPurchaseRepresentation={fianceCanPurchaseRepresentation}
            maxRepresentationProductsCanPurchase={
              maxRepresentationProductsCanPurchase
            }
            isQAProductAvailable={isQAProductAvailable}
          />
        )}
      </>
    );
  }

  return !isPrenupPaid ? (
    <AttorneyServicesUnpaid
      isMobile={isMobile}
      isLoading={isLoading}
      stateName={document.state.name}
    />
  ) : (
    <>
      <StyledAttorneySecondaryButton
        data-testid="attorney-services-button"
        ref={buttonRef}
        style={{ opacity: isLoading ? 0 : 1 }}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <Attorney />
        Attorney services
      </StyledAttorneySecondaryButton>
      {isOpen && (
        <AttorneyServicesCard
          user={user}
          currentUserWillHireAttorney={!!currentUserWillHireAttorney}
          products={products}
          setIsOpen={setIsOpen}
          buttonRef={buttonRef}
          userDocument={document}
          answersWithAttorneyRestrictions={answersWithAttorneyRestrictions}
          representationPayment={representationPayment}
          qAPayment={qAPayment}
          isQAPending={isQAPending}
          userCanPurchaseRepresentation={userCanPurchaseRepresentation}
          fianceCanPurchaseRepresentation={fianceCanPurchaseRepresentation}
          maxRepresentationProductsCanPurchase={
            maxRepresentationProductsCanPurchase
          }
          isQAProductAvailable={isQAProductAvailable}
        />
      )}
    </>
  );
};

export default AttorneyServicesComponent;
